/**
 * Image Handler Utility
 *
 * This utility provides standardized functions for handling images in the calendar system.
 * It consolidates all image-related functionality to ensure consistent behavior across components.
 */

import React from 'react'
import { ExtendedEvent, GameMetadata, ProcessedGame, Game } from '../types'

/**
 * Default placeholder image to use when no image is available
 */
export const PLACEHOLDER_IMAGE = '/placeholder-game.png'

/**
 * Formats an image URL to ensure it's properly formatted
 *
 * @param url The image URL to format
 * @param fallbackUrl Optional fallback URL if the primary URL is invalid
 * @returns A properly formatted URL or a placeholder
 */
export const formatImageUrl = (
  url: string | null | undefined,
  fallbackUrl: string | null | undefined = null
): string => {
  // If no URL is provided, try the fallback or return placeholder
  if (!url) {
    return fallbackUrl ? formatImageUrl(fallbackUrl) : PLACEHOLDER_IMAGE
  }

  // Handle relative URLs
  if (url.startsWith('/')) {
    return url
  }

  // Convert HTTP to HTTPS for security
  if (url.startsWith('http:')) {
    return url.replace('http:', 'https:')
  }

  // Handle partial URLs from BoardGameGeek
  if (!url.startsWith('http') && !url.startsWith('/')) {
    // If it's a BGG image ID, construct the full URL
    if (/^\d+/.test(url)) {
      return `https://boardgamegeek.com/image/${url}`
    }
    // Otherwise, assume it's a partial URL missing the protocol
    return `https:${url}`
  }

  return url
}

/**
 * Gets the game image URL from an event, checking all possible sources
 *
 * @param event The event object
 * @returns A formatted game image URL
 */
export const getGameImageUrl = (event: ExtendedEvent): string => {
  const { extendedProps } = event

  if (!extendedProps) {
    return PLACEHOLDER_IMAGE
  }

  // For placeholder events with BGG game data
  if (extendedProps.isPlaceholder && extendedProps.metadata?.game?.image_url) {
    return formatImageUrl(extendedProps.metadata.game.image_url)
  }

  // Check all possible sources for the game image in order of preference
  const sources = [
    extendedProps.gameImageUrl,
    extendedProps.game?.image_url,
    extendedProps.game?.thumbnail_url,
    extendedProps.metadata?.game?.image_url,
    extendedProps.metadata?.game?.thumbnail_url,
  ]

  // Find the first valid source
  for (const source of sources) {
    if (source) {
      return formatImageUrl(source)
    }
  }

  return PLACEHOLDER_IMAGE
}

/**
 * Gets the game thumbnail URL from an event
 *
 * @param event The event object
 * @returns A formatted game thumbnail URL
 */
export const getGameThumbnailUrl = (event: ExtendedEvent): string => {
  const { extendedProps } = event

  if (!extendedProps) {
    return PLACEHOLDER_IMAGE
  }

  // Check thumbnail sources first, then fall back to regular images
  const sources = [
    extendedProps.game?.thumbnail_url,
    extendedProps.metadata?.game?.thumbnail_url,
    extendedProps.game?.image_url,
    extendedProps.metadata?.game?.image_url,
    extendedProps.gameImageUrl,
  ]

  // Find the first valid source
  for (const source of sources) {
    if (source) {
      return formatImageUrl(source)
    }
  }

  return PLACEHOLDER_IMAGE
}

/**
 * Gets the game image URL from a game object (either database Game or BGG GameMetadata)
 *
 * @param game The game object
 * @returns A formatted game image URL
 */
export const getGameImageUrlFromGame = (
  game: Game | GameMetadata | ProcessedGame | null | undefined
): string => {
  if (!game) {
    return PLACEHOLDER_IMAGE
  }

  // Check all possible image sources
  const sources = [
    'image_url' in game ? game.image_url : undefined,
    'thumbnail_url' in game ? game.thumbnail_url : undefined,
  ]

  // Find the first valid source
  for (const source of sources) {
    if (source) {
      return formatImageUrl(source)
    }
  }

  return PLACEHOLDER_IMAGE
}

/**
 * Gets the game thumbnail URL from a game object
 *
 * @param game The game object
 * @returns A formatted game thumbnail URL
 */
export const getGameThumbnailUrlFromGame = (
  game: Game | GameMetadata | ProcessedGame | null | undefined
): string => {
  if (!game) {
    return PLACEHOLDER_IMAGE
  }

  // Check thumbnail sources first, then fall back to regular images
  const sources = [
    'thumbnail_url' in game ? game.thumbnail_url : undefined,
    'image_url' in game ? game.image_url : undefined,
  ]

  // Find the first valid source
  for (const source of sources) {
    if (source) {
      return formatImageUrl(source)
    }
  }

  return PLACEHOLDER_IMAGE
}

/**
 * Extracts all image URLs from an array of events
 *
 * @param events Array of events to extract images from
 * @returns Array of image URLs
 */
export const extractImageUrlsFromEvents = (events: ExtendedEvent[]): string[] => {
  const imageUrls: string[] = []

  events.forEach(event => {
    const imageUrl = getGameImageUrl(event)
    const thumbnailUrl = getGameThumbnailUrl(event)

    if (imageUrl !== PLACEHOLDER_IMAGE) {
      imageUrls.push(imageUrl)
    }

    if (thumbnailUrl !== PLACEHOLDER_IMAGE && thumbnailUrl !== imageUrl) {
      imageUrls.push(thumbnailUrl)
    }
  })

  return imageUrls
}

/**
 * Preloads images for better performance
 *
 * @param imageUrls The image URLs to preload
 * @param preloadedImages Set of already preloaded images
 * @returns Updated set of preloaded images
 */
export const preloadImages = (
  imageUrls: string[],
  preloadedImages: Set<string> = new Set()
): Set<string> => {
  const validUrls = imageUrls.filter(url => url && url !== PLACEHOLDER_IMAGE)
  const uniqueUrls = [...new Set(validUrls)]

  // Only preload images we haven't already preloaded
  const newUrls = uniqueUrls.filter(url => !preloadedImages.has(url))
  if (newUrls.length === 0) return preloadedImages

  const updatedPreloadedImages = new Set(preloadedImages)

  newUrls.forEach(url => {
    const img = new Image()
    img.src = url
    img.onerror = () => console.warn('Failed to preload image:', url)
    updatedPreloadedImages.add(url)
  })

  return updatedPreloadedImages
}

/**
 * Creates an image element with fallback handling
 *
 * @param src Primary image source
 * @param alt Alt text for the image
 * @param fallbackSrc Fallback image source
 * @returns Formatted image source URL with fallback handling
 */
export const createImageWithFallback = (
  src: string,
  alt: string,
  fallbackSrc: string = PLACEHOLDER_IMAGE
): string => {
  // Simply return the formatted URL - the component using this will handle the error
  return formatImageUrl(src, fallbackSrc)
}
